import React, { useState, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import { toast } from 'react-hot-toast';
import { Save, Crown, Info, Globe } from 'lucide-react';
import { api } from '../../utils/api';
import { useAuth } from '../../contexts/AuthContext';

// Order + which value fields each benefit exposes. `enforced:false` benefits are
// shown on the app but the "audience" is not enforced server-side (dispatch is the
// external matching engine; the assistant isn't gated) — surfaced as a note.
const BENEFIT_META = [
  { key: 'discountedRides', label: 'Discounted Rides', values: ['ridesPerCycle', 'discountPercent'], enforced: true },
  { key: 'unlimitedVehicles', label: 'Unlimited Vehicles in My Garage', values: ['freeLimit'], enforced: true },
  { key: 'noConfirmationFee', label: 'No Booking Confirmation Fee', values: [], enforced: true },
  { key: 'priorityAllocation', label: 'Priority Driver Allocation', values: [], enforced: false,
    note: 'Enforced for subscribers by the matching engine (priority queue + wider driver search). The engine gates on an active subscription, so the "Everyone" option here is not applied to non-subscribers.' },
  { key: 'voiceAssistant', label: 'Voice / Google Assistant', values: [], enforced: false,
    note: 'Display only — the assistant is not gated on the backend yet.' },
];

const VALUE_META = {
  ridesPerCycle: { label: 'Rides / month', step: 1, min: 0 },
  discountPercent: { label: 'Discount %', step: 1, min: 0, max: 100 },
  freeLimit: { label: 'Free-tier vehicle limit', step: 1, min: 0 },
};

// The customer app's languages. English is the base copy every customer falls
// back to; for the others, a field left empty shows the English text.
const LANGUAGES = [
  { code: 'en', label: 'English' },
  { code: 'ta', label: 'தமிழ் (Tamil)' },
  { code: 'hi', label: 'हिन्दी (Hindi)' },
  { code: 'te', label: 'తెలుగు (Telugu)' },
  { code: 'kn', label: 'ಕನ್ನಡ (Kannada)' },
  { code: 'ml', label: 'മലയാളം (Malayalam)' },
];

export default function SubscriptionPlan() {
  const { hasPermission } = useAuth();
  const qc = useQueryClient();
  const [form, setForm] = useState(null);
  const [lang, setLang] = useState('en'); // language whose copy the fields below edit

  const { data, isLoading } = useQuery(['subscription-plan'], () => api.get('/subscription-plan'));
  const config = data?.data?.data;

  useEffect(() => {
    if (config) {
      // Deep-clone so edits don't mutate the cached query data.
      setForm(JSON.parse(JSON.stringify(config)));
    }
  }, [config]);

  const save = useMutation(
    (payload) => api.put('/subscription-plan', payload),
    {
      onSuccess: () => { toast.success('Subscription plan updated'); qc.invalidateQueries(['subscription-plan']); },
      onError: (e) => toast.error(e?.response?.data?.message || 'Save failed'),
    },
  );

  if (!hasPermission('settings', 'view')) {
    return <div className="p-6 text-gray-500">You do not have permission to view this page.</div>;
  }
  if (isLoading || !form) {
    return <div className="flex justify-center py-20"><div className="loading-spinner" /></div>;
  }

  const canWrite = hasPermission('settings', 'write');

  const setField = (field, value) => setForm((f) => ({ ...f, [field]: value }));
  const setBenefit = (key, field, value) =>
    setForm((f) => ({ ...f, benefits: { ...f.benefits, [key]: { ...f.benefits[key], [field]: value } } }));

  // Copy for the selected language: English lives on the benefit itself, the
  // others under `translations[lang]` (and `nameTranslations[lang]` for the name).
  const isEnglish = lang === 'en';
  const langLabel = LANGUAGES.find((l) => l.code === lang)?.label || lang;
  const copyOf = (b, field) => (isEnglish ? b[field] : b.translations?.[lang]?.[field]) || '';
  const setCopy = (key, field, value) => {
    if (isEnglish) { setBenefit(key, field, value); return; }
    setForm((f) => {
      const b = f.benefits[key];
      const translations = { ...(b.translations || {}) };
      translations[lang] = { ...(translations[lang] || {}), [field]: value };
      return { ...f, benefits: { ...f.benefits, [key]: { ...b, translations } } };
    });
  };
  const setPlanName = (value) => {
    if (isEnglish) { setField('name', value); return; }
    setForm((f) => ({ ...f, nameTranslations: { ...(f.nameTranslations || {}), [lang]: value } }));
  };
  // "3/5" on a language tab — how many benefits have a title written in it.
  const translatedCount = (code) =>
    BENEFIT_META.filter((m) => form.benefits?.[m.key]?.translations?.[code]?.title?.trim()).length;

  const onSave = () => {
    save.mutate({
      name: form.name,
      nameTranslations: form.nameTranslations || {},
      priceInr: Number(form.priceInr) || 0,
      billingPeriod: form.billingPeriod,
      isActive: form.isActive !== false,
      benefits: form.benefits,
    });
  };

  return (
    <div>
      <div className="page-header flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Crown className="text-primary-600" size={24} />
          <div>
            <h1 className="page-title">Subscription Plan</h1>
            <p className="page-subtitle">Configure the NOT Premium plan shown in the customer app.</p>
          </div>
        </div>
        {canWrite && (
          <button className="btn btn-primary flex items-center gap-2" onClick={onSave} disabled={save.isLoading}>
            <Save size={16} /> {save.isLoading ? 'Saving…' : 'Save Changes'}
          </button>
        )}
      </div>

      {/* Language — the plan name and every benefit's title/description switch with it */}
      <div className="card mb-4">
        <div className="card-body">
          <div className="flex items-center gap-2 mb-2 text-sm font-medium text-gray-700">
            <Globe size={16} /> App text language
          </div>
          <div className="flex flex-wrap gap-2">
            {LANGUAGES.map((l) => {
              const on = lang === l.code;
              const done = l.code === 'en' ? null : translatedCount(l.code);
              return (
                <button key={l.code} type="button" onClick={() => setLang(l.code)}
                  className={`px-3 py-1.5 rounded-md text-sm border ${on
                    ? 'bg-primary-600 text-white border-primary-600'
                    : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'}`}>
                  {l.label}
                  {done !== null && <span className={on ? 'text-white/80' : 'text-gray-400'}>{` · ${done}/${BENEFIT_META.length}`}</span>}
                </button>
              );
            })}
          </div>
          <p className="text-xs text-gray-500 mt-2">
            {isEnglish
              ? 'English is the base text. Customers see it in any language that has no text of its own.'
              : `Customers whose app is in ${langLabel} see this text. A field left empty shows the English text.`}
          </p>
        </div>
      </div>

      {/* Plan-level fields */}
      <div className="card mb-4">
        <div className="card-body grid grid-cols-1 md:grid-cols-3 gap-4">
          <div>
            <label className="form-label">Plan name{isEnglish ? '' : ` — ${langLabel}`}</label>
            <input className="input" disabled={!canWrite}
              value={(isEnglish ? form.name : form.nameTranslations?.[lang]) || ''}
              placeholder={isEnglish ? '' : form.name || ''}
              onChange={(e) => setPlanName(e.target.value)} />
          </div>
          <div>
            <label className="form-label">Price (₹) — display only</label>
            <input type="number" className="input" value={form.priceInr ?? ''} disabled={!canWrite}
              onChange={(e) => setField('priceInr', e.target.value)} />
          </div>
          <div>
            <label className="form-label">Billing period</label>
            <select className="input" value={form.billingPeriod || 'MONTHLY'} disabled={!canWrite}
              onChange={(e) => setField('billingPeriod', e.target.value)}>
              <option value="MONTHLY">Monthly</option>
              <option value="YEARLY">Yearly</option>
            </select>
          </div>
        </div>
      </div>

      {/* Benefits */}
      <div className="space-y-4">
        {BENEFIT_META.map((meta) => {
          const b = form.benefits?.[meta.key];
          if (!b) return null;
          return (
            <div key={meta.key} className="card">
              <div className="card-body">
                {/* Header: name + show toggle */}
                <div className="flex items-center justify-between mb-3">
                  <h3 className="card-title">{meta.label}</h3>
                  <label className="inline-flex items-center gap-2 cursor-pointer">
                    <span className="text-sm text-gray-500">Show on app</span>
                    <input type="checkbox" className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
                      checked={!!b.enabled} disabled={!canWrite}
                      onChange={(e) => setBenefit(meta.key, 'enabled', e.target.checked)} />
                  </label>
                </div>

                {!meta.enforced && (
                  <div className="flex items-start gap-2 mb-3 text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-md px-3 py-2">
                    <Info size={14} className="mt-0.5 shrink-0" />
                    <span>{meta.note}</span>
                  </div>
                )}

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {/* Audience */}
                  <div>
                    <label className="form-label">Who gets this benefit</label>
                    <select className="input" value={b.audience || 'subscribers'} disabled={!canWrite}
                      onChange={(e) => {
                        const val = e.target.value;
                        setBenefit(meta.key, 'audience', val);
                        // Warn when "Everyone" is picked on a benefit the engine doesn't
                        // honor for everyone — it silently stays subscribers-only.
                        if (val === 'everyone' && !meta.enforced) {
                          toast(`"Everyone" is not enforced for ${meta.label} — only active subscribers will receive it.`, { icon: '⚠️', duration: 6000 });
                        }
                      }}>
                      <option value="subscribers">Subscribers only</option>
                      <option value="everyone">Everyone</option>
                    </select>
                    {!meta.enforced && b.audience === 'everyone' && (
                      <p className="text-xs text-amber-700 mt-1">
                        ⚠️ “Everyone” isn’t applied for this benefit — the matching engine only gives it to active subscribers.
                      </p>
                    )}
                  </div>

                  {/* Value fields */}
                  {meta.values.map((vk) => (
                    <div key={vk}>
                      <label className="form-label">{VALUE_META[vk].label}</label>
                      <input type="number" className="input"
                        step={VALUE_META[vk].step} min={VALUE_META[vk].min} max={VALUE_META[vk].max}
                        value={b[vk] ?? ''} disabled={!canWrite}
                        onChange={(e) => setBenefit(meta.key, vk, e.target.value === '' ? undefined : Number(e.target.value))} />
                    </div>
                  ))}
                </div>

                {/* Copy */}
                <div className="grid grid-cols-1 gap-3 mt-4">
                  <div>
                    <label className="form-label">Title (app){isEnglish ? '' : ` — ${langLabel}`}</label>
                    <input className="input" value={copyOf(b, 'title')} disabled={!canWrite}
                      placeholder={isEnglish ? '' : b.title || ''}
                      onChange={(e) => setCopy(meta.key, 'title', e.target.value)} />
                  </div>
                  <div>
                    <label className="form-label">Description (app){isEnglish ? '' : ` — ${langLabel}`}</label>
                    <textarea className="input" rows={2} value={copyOf(b, 'body')} disabled={!canWrite}
                      placeholder={isEnglish ? '' : b.body || ''}
                      onChange={(e) => setCopy(meta.key, 'body', e.target.value)} />
                  </div>
                </div>

                {meta.values.length > 0 && (
                  <p className="text-xs text-gray-400 mt-2">
                    Tip: use {'{rides}'}, {'{percent}'}, {'{freeLimit}'} in the title/description, in any language — they’re filled from the values above.
                  </p>
                )}
              </div>
            </div>
          );
        })}
      </div>

      {canWrite && (
        <div className="mt-6 flex justify-end">
          <button className="btn btn-primary flex items-center gap-2" onClick={onSave} disabled={save.isLoading}>
            <Save size={16} /> {save.isLoading ? 'Saving…' : 'Save Changes'}
          </button>
        </div>
      )}
    </div>
  );
}
